import React, { useEffect, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  addItemApi,
  getItemByIdApi,
  updateItemApi,
} from "../services/item.service";
import {
  ITEM_TYPE,
  MANAGE_TYPE,
  SALE_UNITS,
  SELLING_TYPE,
  USAGE_TYPE,
} from "../utils/constants";
import { useGetApi } from "../hooks/useGetApi";
import { getCompanyApi } from "../services/company.service";
import { getCategoryApi } from "../services/category.service";
import { getDepartmentApi } from "../services/department.service";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import Loader from "../components/Loader/loader";
import ItemSchema from "../joi/master/item.schema";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";


export default function MasterItemAdd() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoadingItem, setIsLoadingItem] = useState(false);
  const [itemError, setItemError] = useState(false);

  const handleClose = () => {
    navigate(-1);
  };

  const initialState = {
    itemName: "",
    itemCode: "",
    HSNCode: "",
    itemType: "",
    company: "",
    department: "",
    category: "",
    subCategory: "",
    manageType: "",
    purchaseUnit: "",
    purchaseQuantity: 1,
    saleUnit: "",
    saleQuantity: 1,
    claimRate: 0,
    discountLevel1: "",
    discountLevel2: "",
    normalRate: 0,
    minQty: 0,
    maxQty: 0,
    sellingType: "",
    usageType: "",
    CPTCode: "",
    location: "",
    batchCode: "",
    barcode: "",
    active: true,
    isReturnable: false,
    favourite: false,
    isBreakSaleNotAllowed: false,
    isInclusiveTax: false,
    withoutRxNotSale: false,
    isPoc: false,
    mandatoryExpiryDate: false,
  };

  const [formData, setFormData] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);

  // companyList
  const { dataList: companyList } = useGetApi({
    apiFunction: getCompanyApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "companies",
  });

  // departmentList
  const { dataList: departmentList } = useGetApi({
    apiFunction: getDepartmentApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
  });

  // categoryList
  const { dataList: categoryList } = useGetApi({
    apiFunction: getCategoryApi,
    params: { limit: Number.MAX_SAFE_INTEGER, type: "category" },
    dataKey: "categories",
  });

  // subCategoryList
  const { dataList: subCategoryList } = useGetApi({
    apiFunction: getCategoryApi,
    params: { limit: Number.MAX_SAFE_INTEGER, type: "subcategory" },
    dataKey: "categories",
  });

  // Fetch item data for editing
  const fetchItem = async () => {
    if (!id) return;
    
    setIsLoadingItem(true);
    setItemError(false);
    setIsEditMode(true);
    
    try {
      const response = await getItemByIdApi({ id });
      
      if (response?.code === 200 && response?.data) {
        console.log("Fetched item data:", response.data);
        setFormData({
          ...initialState,
          _id: response.data._id,
          itemName: response.data?.itemName || "",
          itemCode: response.data?.itemCode || "",
          HSNCode: response.data?.HSNCode || "",
          itemType: response.data?.itemType || "",
          company: response.data?.company || null, // Keep as object for Autocomplete
          department: response.data?.department || null, // Keep as object for Autocomplete
          category: response.data?.category || null, // Keep as object for Autocomplete
          subCategory: response.data?.subCategory || null, // Keep as object for Autocomplete
          manageType: response.data?.manageType || "",
          purchaseUnit: response.data?.purchaseUnit || "",
          purchaseQuantity: response.data?.purchaseQuantity ?? 1,
          saleUnit: response.data?.saleUnit || "",
          saleQuantity: response.data?.saleQuantity ?? 1,
          claimRate: response.data?.claimRate ?? 0,
          discountLevel1: response.data?.discountLevel1 || "",
          discountLevel2: response.data?.discountLevel2 || "",
          normalRate: response.data?.normalRate ?? 0,
          minQty: response.data?.minQty ?? 0,
          maxQty: response.data?.maxQty ?? 0,
          sellingType: response.data?.sellingType || "",
          CPTCode: response.data?.CPTCode || "",
          location: response.data?.location || "",
          batchCode: response.data?.batchCode || "",
          barcode: response.data?.barcode || "",
          active: response.data?.active ?? true,
          isReturnable: response.data?.isReturnable ?? false,
          favourite: response.data?.favourite ?? false,
          isBreakSaleNotAllowed: response.data?.isBreakSaleNotAllowed ?? false,
          isInclusiveTax: response.data?.isInclusiveTax ?? false,
          withoutRxNotSale: response.data?.withoutRxNotSale ?? false,
          isPoc: response.data?.isPoc ?? false,
          mandatoryExpiryDate: response.data?.mandatoryExpiryDate ?? false,
        });

        // Add `usageType` only if manageType is "Inventory"
        if (response.data?.manageType === "Inventory") {
          setFormData(prev => ({
            ...prev,
            usageType: response.data?.usageType || "",
          }));
        }
      } else {
        setItemError(true);
        toast.error("Failed to fetch item data");
      }
    } catch (error) {
      console.error("Error fetching item:", error);
      setItemError(true);
      toast.error("Failed to fetch item data");
    } finally {
      setIsLoadingItem(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchItem();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prevState) => {
      let updatedData = {
        ...prevState,
        [name]:
          type === "checkbox"
            ? checked
            : type === "number"
            ? Number(value)
            : value,
      };

      // If `manageType` is changed to "Non Inventory", remove `usageType`
      if (name === "manageType" && value === "Non Inventory") {
        const { usageType, ...newState } = updatedData;
        updatedData = newState;
      }

      return updatedData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let response;

    // Create a copy of form data to modify
    const formDataToSubmit = { ...formData };

    // Handle company, department, category, subCategory - convert from objects to IDs
    formDataToSubmit.company = formData?.company?._id || formData?.company;
    formDataToSubmit.department = formData?.department?._id || formData?.department;
    formDataToSubmit.category = formData?.category?._id || formData?.category;
    formDataToSubmit.subCategory = formData?.subCategory?._id || formData?.subCategory;

    // If manageType is 'Non Inventory', set optional fields to undefined if they are empty strings
    if (formData.manageType === 'Non Inventory') {
      const optionalFields = [
        'purchaseUnit', 'saleUnit', 'sellingType', 'location', 'purchaseQuantity',
        'saleQuantity', 'claimRate', 'discountLevel1', 'discountLevel2',
        'minQty', 'maxQty', 'usageType', 'CPTCode', 'batchCode', 'barcode'
      ];

      optionalFields.forEach(field => {
        if (formDataToSubmit[field] === '') {
          formDataToSubmit[field] = undefined;
        }
      });
    }

    // Validate form data
    const { error } = ItemSchema.validate(formDataToSubmit, { abortEarly: false });

    if (error) {
      toast.error("Please fill all required fields.");
      return;
    }

    setIsLoading(true);
    try {
      if (formData._id) {
        response = await updateItemApi(formDataToSubmit);
      } else {
        response = await addItemApi(formDataToSubmit);
      }

      if (response?.code === 200) {
        const raw = response?.message;
        const looksLikeHttpCode =
          raw != null && /^\d{1,3}$/.test(String(raw).trim());
        const genericSuccess =
          raw == null ||
          String(raw).trim().toLowerCase() === "success" ||
          looksLikeHttpCode;
        toast.success(
          genericSuccess
            ? `Item ${formData._id ? "updated" : "added"} successfully.`
            : String(raw).trim()
        );
        navigate(-1);
      } else {
        toast.error(response?.message || "Some error occurred.");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        {isLoadingItem ? (
          <Loader />
        ) : itemError ? (
          <ErrorMessage />
        ) : (
          <Box
            component="form"
            noValidate
            sx={{ width: "100%", pr: "90px", py: "20px" }}
            onSubmit={handleSubmit}
          >
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{
                  color: '#5D6A82', 
                  textTransform: 'none', 
                  fontSize: '16px',
                  fontWeight: 'bold',
                  padding: 0,
                  transition: 'none !important',
                  mb: 2
                }}
              >
                Back
              </Button>
              
            {/* enter item details */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: "20px",
              }}
            >
              
              <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
                {`${formData._id ? "Update" : "Enter"} Item Details`}
              </Typography>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            {/* first box */}
            <Box
              sx={{
                backgroundColor: "rgba(240, 247, 252, 1)",
                borderRadius: "12px",
                mb: "30px",
                p: "20px",
              }}
            >
              <Typography sx={{ mb: "20px", color: "rgba(93, 106, 130, 1)" }}>
                Basic Details
              </Typography>
              <Grid container spacing={2}>
                {/* item name */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    name="itemName"
                    label="Item Name"
                    required
                    fullWidth
                    value={formData?.itemName || ""}
                    onChange={handleChange}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                {/* item code */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    name="itemCode"
                    label="Item Code"
                    required
                    fullWidth
                    value={formData?.itemCode || ""}
                    onChange={handleChange}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                {/* hsn code */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    name="HSNCode"
                    label="HSN Code"
                    fullWidth
                    value={formData?.HSNCode || ""}
                    onChange={handleChange}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* second box */}
            <Box
              sx={{
                backgroundColor: "rgba(240, 247, 252, 1)",
                borderRadius: "12px",
                mb: "30px",
                p: "20px",
              }}
            >
              <Typography sx={{ mb: "20px", color: "rgba(93, 106, 130, 1)" }}>
                Classification and Usage
              </Typography>
              <Grid container spacing={2}>
                {/* item type */}
                <Grid item xs={12} sm={6} md={4}>
                  {/* <TextField
                    name="itemType"
                    label="Item Type"
                    required
                    fullWidth
                    value={formData?.itemType || ""}
                    onChange={handleChange}
                    sx={{ bgcolor: "white" }}
                  /> */}
                  <FormControl fullWidth required sx={{ bgcolor: "white" }}>
                    <InputLabel>Item Type</InputLabel>
                    <Select
                      name="itemType"
                      label="Item Type"
                      required
                      value={formData?.itemType || ""}
                      onChange={handleChange}
                    >
                      {ITEM_TYPE.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {/* company */}
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={companyList || []}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="company"
                        label="Company"
                        required={formData?.itemType === "Inventory"}
                        fullWidth
                        inputProps={{
                          ...params.inputProps,
                          ...(formData?.itemType === "Inventory"
                            ? { required: true }
                            : {}),
                        }}
                      />
                    )}
                    value={formData?.company || null}
                    onChange={(_, newValue) =>
                      handleChange({
                        target: { name: "company", value: newValue },
                      })
                    }
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                {/* department */}
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={departmentList || []}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="department"
                        label="Department"
                        required
                        fullWidth
                        inputProps={{
                          ...params.inputProps,
                          required: true,
                        }}
                      />
                    )}
                    value={formData?.department || null}
                    onChange={(_, newValue) =>
                      handleChange({
                        target: { name: "department", value: newValue },
                      })
                    }
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                {/* category */}
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={categoryList || []}
                    getOptionLabel={(option) => option?.categoryName || ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="category"
                        label="Category"
                        required
                        fullWidth
                        inputProps={{
                          ...params.inputProps,
                          required: true,
                        }}
                      />
                    )}
                    value={formData?.category || null}
                    onChange={(_, newValue) =>
                      handleChange({
                        target: { name: "category", value: newValue },
                      })
                    }
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                {/* subCategory */}
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={subCategoryList?.filter(subCat => 
                      formData?.category?._id ? subCat?.parentCategory?._id === formData?.category?._id : true
                    ) || []}
                    getOptionLabel={(option) => option?.categoryName || ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="subCategory"
                        label="Sub Category"
                        required
                        fullWidth
                        inputProps={{
                          ...params.inputProps,
                          required: true,
                        }}
                      />
                    )}
                    value={formData?.subCategory || null}
                    onChange={(_, newValue) =>
                      handleChange({
                        target: { name: "subCategory", value: newValue },
                      })
                    }
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                {/* manageType */}
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={MANAGE_TYPE || []}
                    isOptionEqualToValue={(option, value) => option === value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="manageType"
                        label="Manage Type"
                        required
                        fullWidth
                        inputProps={{
                          ...params.inputProps,
                          required: true,
                        }}
                      />
                    )}
                    value={formData?.manageType || null}
                    onChange={(_, newValue) =>
                      handleChange({
                        target: { name: "manageType", value: newValue },
                      })
                    }
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* third box - Pricing Details */}
            <Box
              sx={{
                backgroundColor: "rgba(240, 247, 252, 1)",
                borderRadius: "12px",
                mb: "30px",
                p: "20px",
              }}
            >
              <Typography sx={{ mb: "20px", color: "rgba(93, 106, 130, 1)" }}>
                Pricing Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Grid container spacing={1}>
                    {/* purchaseUnit */}
                    <Grid item xs={12} sm={6}>
                      <FormControl
                        fullWidth
                        required={formData?.manageType !== "Non Inventory"}
                        sx={{ bgcolor: "white" }}
                      >
                        <InputLabel>Purchase Unit</InputLabel>
                        <Select
                          name="purchaseUnit"
                          label="Purchase Unit"
                          value={formData?.purchaseUnit || ""}
                          onChange={handleChange}
                          required={formData?.manageType !== "Non Inventory"}
                          disabled={formData?.manageType === "Non Inventory"}
                        >
                          {SALE_UNITS.map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {unit}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* purchaseQuantity */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="purchaseQuantity"
                        label="Purchase Quantity"
                        type="number"
                        required={formData?.manageType !== "Non Inventory"}
                        disabled={formData?.manageType === "Non Inventory"}
                        fullWidth
                        value={formData?.purchaseQuantity || ""}
                        onChange={handleChange}
                        sx={{ bgcolor: "white" }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Grid container spacing={1}>
                    {/* saleUnit */}
                    <Grid item xs={12} sm={6}>
                      <FormControl
                        fullWidth
                        required={formData?.manageType !== "Non Inventory"}
                        sx={{ bgcolor: "white" }}
                      >
                        <InputLabel>Sale Unit</InputLabel>
                        <Select
                          name="saleUnit"
                          label="Sale Unit"
                          value={formData?.saleUnit || ""}
                          onChange={handleChange}
                          required={formData?.manageType !== "Non Inventory"}
                          disabled={formData?.manageType === "Non Inventory"}
                        >
                          {SALE_UNITS.map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {unit}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* saleQuantity */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="saleQuantity"
                        label="Sale Quantity"
                        type="number"
                        required={formData?.manageType !== "Non Inventory"}
                        disabled={formData?.manageType === "Non Inventory"}
                        fullWidth
                        value={formData?.saleQuantity || ""}
                        onChange={handleChange}
                        sx={{ bgcolor: "white" }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* normalRate */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    name="normalRate"
                    label="Normal Rate"
                    type="number"
                    required
                    fullWidth
                    value={formData?.normalRate || ""}
                    onChange={handleChange}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                {/* claimRate */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    name="claimRate"
                    label="Claim Rate"
                    type="number"
                    required={formData?.manageType !== "Non Inventory"}
                    disabled={formData?.manageType === "Non Inventory"}
                    fullWidth
                    value={formData?.claimRate || ""}
                    onChange={handleChange}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                {/* discountLevel1 */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    name="discountLevel1"
                    label="Discount Level 1"
                    disabled={formData?.manageType === "Non Inventory"}
                    fullWidth
                    value={formData?.discountLevel1 || ""}
                    onChange={handleChange}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                {/* discountLevel2 */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    name="discountLevel2"
                    label="Discount Level 2"
                    disabled={formData?.manageType === "Non Inventory"}
                    fullWidth
                    value={formData?.discountLevel2 || ""}
                    onChange={handleChange}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                {/* minQty */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    name="minQty"
                    label="Min Qty"
                    type="number"
                    required={formData?.manageType !== "Non Inventory"}
                    disabled={formData?.manageType === "Non Inventory"}
                    fullWidth
                    value={formData?.minQty || ""}
                    onChange={handleChange}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                {/* maxQty */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    name="maxQty"
                    label="Max Qty"
                    type="number"
                    required={formData?.manageType !== "Non Inventory"}
                    disabled={formData?.manageType === "Non Inventory"}
                    fullWidth
                    value={formData?.maxQty || ""}
                    onChange={handleChange}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>

                {/* mandatory expiry date */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="mandatoryExpiryDate"
                        checked={formData?.mandatoryExpiryDate || false}
                        onChange={handleChange}
                        disabled={formData?.manageType === "Non Inventory"}
                      />
                    }
                    sx={{ height: "57px" }}
                    label="Mandatory Expiry Date"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* fourth box - Status and Special Flags */}
            <Box
              sx={{
                backgroundColor: "rgba(240, 247, 252, 1)",
                borderRadius: "12px",
                mb: "30px",
                p: "20px",
              }}
            >
              <Typography sx={{ mb: "20px", color: "rgba(93, 106, 130, 1)" }}>
                Status and Special Flags
              </Typography>
              <Grid container spacing={2}>
                {/* sellingType */}
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={SELLING_TYPE || []}
                    isOptionEqualToValue={(option, value) => option === value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="sellingType"
                        label="Selling Type"
                        required={formData?.manageType !== "Non Inventory"}
                        fullWidth
                        inputProps={{
                          ...params.inputProps,
                          ...(formData?.manageType !== "Non Inventory"
                            ? { required: true }
                            : {}),
                        }}
                      />
                    )}
                    value={formData?.sellingType || null}
                    onChange={(_, newValue) =>
                      handleChange({
                        target: { name: "sellingType", value: newValue },
                      })
                    }
                    disabled={formData?.manageType === "Non Inventory"}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                {/* usageType */}
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={USAGE_TYPE || []}
                    isOptionEqualToValue={(option, value) => option === value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="usageType"
                        label="Usage Type"
                        fullWidth
                      />
                    )}
                    value={formData?.usageType || null}
                    onChange={(_, newValue) =>
                      handleChange({
                        target: { name: "usageType", value: newValue },
                      })
                    }
                    disabled={formData?.manageType === "Non Inventory"}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                {/* batchCode */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    name="batchCode"
                    label="Batch Code"
                    fullWidth
                    value={formData?.batchCode || ""}
                    onChange={handleChange}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                {/* CPTCode */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    name="CPTCode"
                    label="CPT Code"
                    fullWidth
                    value={formData?.CPTCode || ""}
                    onChange={handleChange}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                {/* location */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    name="location"
                    label="Location"
                    required={formData?.manageType !== "Non Inventory"}
                    disabled={formData?.manageType === "Non Inventory"}
                    fullWidth
                    value={formData?.location || ""}
                    onChange={handleChange}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>
                {/* barcode */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    name="barcode"
                    label="Bar Code"
                    fullWidth
                    value={formData?.barcode || ""}
                    onChange={handleChange}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>

                {/* active*/}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="active"
                        checked={formData?.active || false}
                        onChange={handleChange}
                      />
                    }
                    label="Active"
                  />
                </Grid>

                {/* isReturnable*/}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isReturnable"
                        checked={formData?.isReturnable || false}
                        onChange={handleChange}
                      />
                    }
                    label="Is Returnable"
                  />
                </Grid>

                {/* favourite*/}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="favourite"
                        checked={formData?.favourite || false}
                        onChange={handleChange}
                      />
                    }
                    label="Favourite"
                  />
                </Grid>

                {/* isBreakSaleNotAllowed*/}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isBreakSaleNotAllowed"
                        checked={formData?.isBreakSaleNotAllowed || false}
                        onChange={handleChange}
                      />
                    }
                    label="Is Break Sale Not Allowed"
                  />
                </Grid>

                {/* isInclusiveTax*/}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isInclusiveTax"
                        checked={formData?.isInclusiveTax || false}
                        onChange={handleChange}
                      />
                    }
                    label="Inclusive Tax?"
                  />
                </Grid>

                {/* withoutRxNotSale*/}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="withoutRxNotSale"
                        checked={formData?.withoutRxNotSale || false}
                        onChange={handleChange}
                      />
                    }
                    label="Without Rx Not Sale"
                  />
                </Grid>

                {/* isPoc*/}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isPoc"
                        checked={formData?.isPoc || false}
                        onChange={handleChange}
                      />
                    }
                    label="Is Poc?"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* save button */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
              <Button
                variant="contained"
                type="submit"
                sx={{
                  textTransform: "none",
                  backgroundColor: "rgba(1, 121, 176, 1)",
                  width: "150px",
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : formData._id ? (
                  "Update"
                ) : (
                  `Register`
                )}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
}
